import React from 'react';
import { Chip } from '@mui/material';
import { RESOURCES } from '../../hooks/usePermissions';
import { ledgerAPI } from '../../services/api';
import {
  AccountPicker,
  BranchPicker,
  CostCentrePicker,
  CurrencyPicker,
  DepartmentPicker,
  DimensionValuePicker,
  PaymentMethodPicker,
  PaymentMethodTypePicker,
} from './LedgerPickers';

const boolChip = (value, trueLabel = 'Yes', falseLabel = 'No') => (
  <Chip label={value ? trueLabel : falseLabel} color={value ? 'success' : 'default'} size="small" />
);

const activeChip = (value) => (
  <Chip label={value ? 'Active' : 'Inactive'} color={value ? 'success' : 'default'} size="small" />
);

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const dropBlanks = (obj) => {
  const out = { ...obj };
  Object.keys(out).forEach((k) => {
    if (out[k] === '' || out[k] === undefined) delete out[k];
  });
  return out;
};

const taxTypeOptions = async () => {
  const res = await ledgerAPI.getTaxTypes({ limit: 100 });
  const payload = res?.data?.data ?? res?.data ?? {};
  const list = Array.isArray(payload) ? payload : payload.items ?? [];
  return list.map((t) => ({
    label: `${t.tax_type_code || ''} - ${t.tax_type_name || t.tax_type_id}`.trim(),
    value: t.tax_type_id ?? t.id,
  }));
};

const dimensionOptions = async () => {
  const res = await ledgerAPI.getDimensions({ limit: 100 });
  const payload = res?.data?.data ?? res?.data ?? {};
  const list = Array.isArray(payload) ? payload : payload.items ?? [];
  return list.map((d) => ({
    label: `${d.dimension_code || ''} - ${d.dimension_name || d.dimension_id}`.trim(),
    value: d.dimension_id ?? d.id,
  }));
};

export const OBJECT_TYPES = [
  'JOURNAL_ENTRY', 'SALES_INVOICE', 'PURCHASE_INVOICE', 'SALES_RECEIPT',
  'SUPPLIER_PAYMENT', 'CUSTOMER_RECEIPT', 'CREDIT_NOTE', 'DEBIT_NOTE',
  'BANK_TRANSFER', 'BUDGET_TRANSFER', 'ACCRUAL', 'BAD_DEBT_WRITEOFF',
  'DOUBTFUL_PROVISION', 'DEPRECIATION_RUN', 'GOODS_RECEIPT', 'GOODS_ISSUE',
  'STOCK_ADJUSTMENT', 'STOCK_TRANSFER', 'INVENTORY_REVALUATION', 'PAYROLL_RUN',
  'PAYROLL_ADVANCE', 'EXPENSE_CLAIM', 'EXPENSE_REIMBURSEMENT', 'COST_ENTRY',
  'ALLOCATION_RUN', 'INTERCOMPANY_INVOICE', 'INTERCOMPANY_PAYMENT',
  'ELIMINATION_RUN', 'REVENUE_SCHEDULE', 'CASH_RECEIPT', 'CASH_PAYMENT',
  'WHT_CERTIFICATE', 'TAX_RETURN', 'FIXED_ASSET', 'VOUCHER',
  'RECURRING_JOURNAL', 'PAYMENT_BATCH', 'BANK_STATEMENT', 'SUBLEDGER_RECON',
  'BAD_DEBT_RECOVERY', 'OTHER',
];

const RESET_PERIODS = ['NONE', 'MONTHLY', 'QUARTERLY', 'YEARLY'];
const TAX_KINDS = ['VAT', 'WITHHOLDING_TAX', 'PAYE', 'CORPORATE_TAX', 'EXCISE_DUTY', 'LOCAL_SERVICE_TAX', 'OTHER'];

const numberingDescriptor = {
  resource: RESOURCES.LEDGER.SETTINGS,
  title: 'Numbering Sequences',
  subtitle: 'Document numbering per document type',
  idKey: 'sequence_id',
  searchPlaceholder: 'Search by code or name...',
  columns: [
    { label: 'Code', render: (s) => <strong>{s.sequence_code}</strong> },
    { label: 'Name', render: (s) => s.sequence_name },
    { label: 'Document Type', render: (s) => s.object_type },
    { label: 'Prefix', render: (s) => s.prefix || '-' },
    { label: 'Next', render: (s) => s.next_number ?? s.current_number ?? '-' },
    { label: 'Status', render: (s) => activeChip(s.is_active) },
  ],
  fields: [
    { key: 'sequence_code', label: 'Sequence Code', required: true, helper: 'Unique, e.g. SI-2026' },
    { key: 'sequence_name', label: 'Sequence Name', required: true },
    {
      key: 'object_type', label: 'Document Type', type: 'select', required: true,
      options: OBJECT_TYPES.map((t) => ({ label: t, value: t })),
    },
    { key: 'prefix', label: 'Prefix', required: true, helper: 'e.g. SI-' },
    { key: 'suffix', label: 'Suffix' },
    { key: 'start_number', label: 'Start Number', type: 'number', min: 1 },
    { key: 'step', label: 'Step', type: 'number', min: 1 },
    { key: 'padding_length', label: 'Padding Length', type: 'number', min: 0 },
    {
      key: 'reset_period', label: 'Reset Period', type: 'select',
      options: RESET_PERIODS.map((t) => ({ label: t, value: t })),
    },
    { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
  ],
  initialValues: {
    sequence_code: '', sequence_name: '', object_type: '', prefix: '', suffix: '',
    start_number: 1, step: 1, padding_length: 5, reset_period: 'NONE', is_active: true,
  },
  fetchList: (params) => ledgerAPI.getNumberingSequences(params),
  createItem: (payload) => ledgerAPI.createNumberingSequence(payload),
  updateItem: (id, payload) => ledgerAPI.updateNumberingSequence(id, payload),
  deleteItem: (id) => ledgerAPI.deleteNumberingSequence(id),
  deleteMessage: (item) => `Delete sequence ${item.sequence_code}?`,
  mapToForm: (item) => ({
    sequence_code: item.sequence_code || '',
    sequence_name: item.sequence_name || '',
    object_type: item.object_type || '',
    prefix: item.prefix ?? '',
    suffix: item.suffix ?? '',
    start_number: item.start_number ?? 1,
    step: item.step ?? 1,
    padding_length: item.padding_length ?? 5,
    reset_period: item.reset_period || 'NONE',
    is_active: item.is_active !== false,
  }),
  mapToPayload: (values) => {
    const payload = {
      ...values,
      start_number: num(values.start_number, 1),
      step: num(values.step, 1),
      padding_length: num(values.padding_length ?? 5, 5),
    };
    return dropBlanks(payload);
  },
  validate: (values) => {
    for (const key of ['sequence_code', 'sequence_name', 'object_type', 'prefix']) {
      if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
    }
    return null;
  },
};

export const configDescriptors = {
  numbering: numberingDescriptor,

  paymentMethods: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Payment Methods',
    subtitle: 'How money moves - cash, bank, mobile money',
    idKey: 'method_id',
    searchPlaceholder: 'Search payment methods...',
    columns: [
      { label: 'Name', render: (m) => <strong>{m.method_name}</strong> },
      { label: 'Description', render: (m) => m.description || '-' },
      { label: 'Status', render: (m) => activeChip(m.is_active) },
    ],
    fields: [
      { key: 'method_name', label: 'Method Name', required: true, md: 12 },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: { method_name: '', description: '', is_active: true },
    fetchList: (params) => ledgerAPI.getPaymentMethods(params),
    createItem: (payload) => ledgerAPI.createPaymentMethod(payload),
    updateItem: (id, payload) => ledgerAPI.updatePaymentMethod(id, payload),
    deleteItem: (id) => ledgerAPI.deletePaymentMethod(id),
    deleteMessage: (item) => `Delete payment method ${item.method_name}?`,
    mapToPayload: (values) => dropBlanks({ ...values }),
    validate: (values) => (!String(values.method_name ?? '').trim() ? 'method name is required' : null),
  },

  postingRules: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Posting Rules',
    subtitle: 'Automatic debit/credit legs per source document',
    idKey: 'rule_id',
    searchPlaceholder: 'Search by code or name...',
    columns: [
      { label: 'Code', render: (r) => <strong>{r.rule_code}</strong> },
      { label: 'Name', render: (r) => r.rule_name },
      { label: 'Source Type', render: (r) => r.source_type },
      { label: 'Status', render: (r) => activeChip(r.is_active) },
    ],
    fields: [
      { key: 'rule_code', label: 'Rule Code', required: true },
      { key: 'rule_name', label: 'Rule Name', required: true },
      { key: 'source_type', label: 'Source Type', required: true, helper: 'e.g. SALES_INVOICE' },
      { key: 'trigger_account_id', label: 'Trigger Account', type: 'picker', picker: AccountPicker },
      { key: 'debit_account_id', label: 'Debit Account', type: 'picker', picker: AccountPicker },
      { key: 'credit_account_id', label: 'Credit Account', type: 'picker', picker: AccountPicker },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      rule_code: '', rule_name: '', source_type: '', trigger_account_id: '',
      debit_account_id: '', credit_account_id: '', description: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getPostingRules(params),
    createItem: (payload) => ledgerAPI.createPostingRule(payload),
    updateItem: (id, payload) => ledgerAPI.updatePostingRule(id, payload),
    deleteItem: (id) => ledgerAPI.deletePostingRule(id),
    deleteMessage: (item) => `Delete posting rule ${item.rule_code}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      ['trigger_account_id', 'debit_account_id', 'credit_account_id'].forEach((k) => {
        if (payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      return payload;
    },
    validate: (values) => {
      for (const key of ['rule_code', 'rule_name', 'source_type']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  journalTemplates: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Journal Templates',
    subtitle: 'Reusable journal skeletons (lines are managed on the template record)',
    idKey: 'template_id',
    searchPlaceholder: 'Search by code or name...',
    columns: [
      { label: 'Code', render: (t) => <strong>{t.template_code}</strong> },
      { label: 'Name', render: (t) => t.template_name },
      { label: 'Entry Type', render: (t) => t.entry_type },
      { label: 'Status', render: (t) => activeChip(t.is_active) },
    ],
    fields: [
      { key: 'template_code', label: 'Template Code', required: true },
      { key: 'template_name', label: 'Template Name', required: true },
      {
        key: 'entry_type', label: 'Entry Type', type: 'select', required: true,
        options: ['GENERAL', 'ADJUSTMENT', 'OPENING', 'CLOSING', 'REVERSAL', 'RECLASSIFICATION'].map((t) => ({ label: t, value: t })),
      },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: { template_code: '', template_name: '', entry_type: 'GENERAL', description: '', is_active: true, lines: [] },
    fetchList: (params) => ledgerAPI.getJournalTemplates(params),
    createItem: (payload) => ledgerAPI.createJournalTemplate(payload),
    updateItem: (id, payload) => ledgerAPI.updateJournalTemplate(id, payload),
    deleteItem: (id) => ledgerAPI.deleteJournalTemplate(id),
    deleteMessage: (item) => `Delete template ${item.template_code}?`,
    mapToPayload: (values, isEdit) => {
      const payload = dropBlanks({ ...values });
      if (!isEdit && !payload.lines) payload.lines = [];
      return payload;
    },
    validate: (values) => {
      for (const key of ['template_code', 'template_name', 'entry_type']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  currencies: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Currencies',
    idKey: 'currency_id',
    searchPlaceholder: 'Search currencies...',
    columns: [
      { label: 'Code', render: (c) => <strong>{c.currency_code}</strong> },
      { label: 'Name', render: (c) => c.currency_name },
      { label: 'Symbol', render: (c) => c.symbol || '-' },
      { label: 'Decimals', render: (c) => c.decimal_places ?? '-' },
      { label: 'Status', render: (c) => activeChip(c.is_active) },
    ],
    fields: [
      { key: 'currency_code', label: 'Code', required: true, helper: 'ISO, e.g. UGX' },
      { key: 'currency_name', label: 'Name', required: true },
      { key: 'symbol', label: 'Symbol' },
      { key: 'decimal_places', label: 'Decimal Places', type: 'number', min: 0 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: { currency_code: '', currency_name: '', symbol: '', decimal_places: 2, is_active: true },
    fetchList: (params) => ledgerAPI.getCurrencies(params),
    createItem: (payload) => ledgerAPI.createCurrency(payload),
    updateItem: (id, payload) => ledgerAPI.updateCurrency(id, payload),
    deleteItem: (id) => ledgerAPI.deleteCurrency(id),
    deleteMessage: (item) => `Delete currency ${item.currency_code}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.decimal_places !== undefined) payload.decimal_places = num(payload.decimal_places, 2);
      return payload;
    },
    validate: (values) => {
      for (const key of ['currency_code', 'currency_name']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  exchangeRates: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Exchange Rates',
    idKey: 'rate_id',
    searchPlaceholder: 'Search exchange rates...',
    columns: [
      { label: 'From', render: (r) => r.from_currency_id },
      { label: 'To', render: (r) => r.to_currency_id },
      { label: 'Rate', render: (r) => r.rate },
      { label: 'Effective', render: (r) => (r.effective_date ? new Date(r.effective_date).toLocaleDateString() : '-') },
      { label: 'Status', render: (r) => activeChip(r.is_active) },
    ],
    fields: [
      { key: 'from_currency_id', label: 'From Currency', type: 'picker', picker: CurrencyPicker, required: true },
      { key: 'to_currency_id', label: 'To Currency', type: 'picker', picker: CurrencyPicker, required: true },
      { key: 'rate', label: 'Rate', type: 'number', required: true },
      { key: 'effective_date', label: 'Effective Date', type: 'date', required: true },
      { key: 'expiry_date', label: 'Expiry Date', type: 'date' },
      { key: 'source', label: 'Source' },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      from_currency_id: '', to_currency_id: '', rate: '', effective_date: '', expiry_date: '', source: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getExchangeRates(params),
    createItem: (payload) => ledgerAPI.createExchangeRate(payload),
    updateItem: (id, payload) => ledgerAPI.updateExchangeRate(id, payload),
    deleteItem: (id) => ledgerAPI.deleteExchangeRate(id),
    deleteMessage: () => 'Delete this exchange rate?',
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      ['from_currency_id', 'to_currency_id'].forEach((k) => {
        if (payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      if (payload.rate !== undefined) payload.rate = Number(payload.rate);
      return payload;
    },
    validate: (values) => {
      for (const key of ['from_currency_id', 'to_currency_id', 'rate', 'effective_date']) {
        if (values[key] === '' || values[key] === undefined || values[key] === null) {
          return `${key.replace(/_/g, ' ')} is required`;
        }
      }
      return null;
    },
  },

  taxTypes: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Tax Types',
    idKey: 'tax_type_id',
    searchPlaceholder: 'Search tax types...',
    columns: [
      { label: 'Code', render: (t) => <strong>{t.tax_type_code}</strong> },
      { label: 'Name', render: (t) => t.tax_type_name },
      { label: 'Kind', render: (t) => t.kind },
      { label: 'Status', render: (t) => activeChip(t.is_active) },
    ],
    fields: [
      { key: 'tax_type_code', label: 'Code', required: true },
      { key: 'tax_type_name', label: 'Name', required: true },
      {
        key: 'kind', label: 'Kind', type: 'select', required: true,
        options: TAX_KINDS.map((t) => ({ label: t, value: t })),
      },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: { tax_type_code: '', tax_type_name: '', kind: '', description: '', is_active: true },
    fetchList: (params) => ledgerAPI.getTaxTypes(params),
    createItem: (payload) => ledgerAPI.createTaxType(payload),
    updateItem: (id, payload) => ledgerAPI.updateTaxType(id, payload),
    deleteItem: (id) => ledgerAPI.deleteTaxType(id),
    deleteMessage: (item) => `Delete tax type ${item.tax_type_code}?`,
    mapToPayload: (values) => dropBlanks({ ...values }),
    validate: (values) => {
      for (const key of ['tax_type_code', 'tax_type_name', 'kind']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  taxRates: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Tax Rates',
    idKey: 'tax_rate_id',
    searchPlaceholder: 'Search tax rates...',
    parentFilter: { key: 'tax_type_id', label: 'Tax Type', required: true, fetchOptions: taxTypeOptions },
    columns: [
      { label: 'Name', render: (r) => <strong>{r.rate_name}</strong> },
      { label: 'Rate', render: (r) => `${r.rate}%` },
      { label: 'Effective From', render: (r) => (r.effective_from ? new Date(r.effective_from).toLocaleDateString() : '-') },
      { label: 'Status', render: (r) => activeChip(r.is_active) },
    ],
    fields: [
      { key: 'rate_name', label: 'Rate Name', required: true },
      { key: 'rate', label: 'Rate %', type: 'number', required: true },
      { key: 'effective_from', label: 'Effective From', type: 'date', required: true },
      { key: 'effective_to', label: 'Effective To', type: 'date' },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: { rate_name: '', rate: '', effective_from: '', effective_to: '', is_active: true },
    fetchList: (params) => ledgerAPI.getTaxRates(params),
    createItem: (payload) => ledgerAPI.createTaxRate(payload),
    updateItem: (id, payload) => ledgerAPI.updateTaxRate(id, payload),
    deleteItem: (id) => ledgerAPI.deleteTaxRate(id),
    deleteMessage: (item) => `Delete rate ${item.rate_name}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.rate !== undefined) payload.rate = Number(payload.rate);
      return payload;
    },
    validate: (values) => {
      for (const key of ['rate_name', 'rate', 'effective_from']) {
        if (values[key] === '' || values[key] === undefined || values[key] === null) {
          return `${key.replace(/_/g, ' ')} is required`;
        }
      }
      return null;
    },
  },

  taxCodes: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Tax Codes',
    idKey: 'tax_code_id',
    searchPlaceholder: 'Search tax codes...',
    parentFilter: { key: 'tax_type_id', label: 'Tax Type', required: true, fetchOptions: taxTypeOptions },
    columns: [
      { label: 'Code', render: (t) => <strong>{t.tax_code}</strong> },
      { label: 'Name', render: (t) => t.tax_name },
      { label: 'Rate', render: (t) => `${t.rate}%` },
      { label: 'Status', render: (t) => activeChip(t.is_active) },
    ],
    fields: [
      { key: 'tax_code', label: 'Code', required: true },
      { key: 'tax_name', label: 'Name', required: true },
      { key: 'rate', label: 'Rate %', type: 'number', required: true },
      { key: 'input_tax_account_id', label: 'Input Tax Account', type: 'picker', picker: AccountPicker },
      { key: 'output_tax_account_id', label: 'Output Tax Account', type: 'picker', picker: AccountPicker },
      { key: 'expense_account_id', label: 'Expense Account', type: 'picker', picker: AccountPicker },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      tax_code: '', tax_name: '', rate: '', input_tax_account_id: '',
      output_tax_account_id: '', expense_account_id: '', description: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getTaxCodes(params),
    createItem: (payload) => ledgerAPI.createTaxCode(payload),
    updateItem: (id, payload) => ledgerAPI.updateTaxCode(id, payload),
    deleteItem: (id) => ledgerAPI.deleteTaxCode(id),
    deleteMessage: (item) => `Delete tax code ${item.tax_code}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.rate !== undefined) payload.rate = Number(payload.rate);
      ['input_tax_account_id', 'output_tax_account_id', 'expense_account_id'].forEach((k) => {
        if (payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      return payload;
    },
    validate: (values) => {
      for (const key of ['tax_code', 'tax_name', 'rate']) {
        if (values[key] === '' || values[key] === undefined || values[key] === null) {
          return `${key.replace(/_/g, ' ')} is required`;
        }
      }
      return null;
    },
  },

  taxExemptions: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Tax Exemptions',
    idKey: 'exemption_id',
    searchPlaceholder: 'Search by code, TIN or party...',
    parentFilter: { key: 'tax_type_id', label: 'Tax Type', required: true, fetchOptions: taxTypeOptions },
    columns: [
      { label: 'Code', render: (e) => <strong>{e.exemption_code}</strong> },
      { label: 'Party', render: (e) => e.party_name || e.party_tin },
      { label: 'TIN', render: (e) => e.party_tin },
      { label: 'Status', render: (e) => activeChip(e.is_active) },
    ],
    fields: [
      { key: 'exemption_code', label: 'Code', required: true },
      { key: 'party_tin', label: 'Party TIN', required: true },
      { key: 'party_name', label: 'Party Name' },
      { key: 'effective_from', label: 'Effective From', type: 'date', required: true },
      { key: 'effective_to', label: 'Effective To', type: 'date' },
      { key: 'reason', label: 'Reason', type: 'textarea', md: 12 },
      { key: 'reference_number', label: 'Reference Number' },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      exemption_code: '', party_tin: '', party_name: '', tax_type_id: '',
      effective_from: '', effective_to: '', reason: '', reference_number: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getTaxExemptions(params),
    createItem: (payload) => ledgerAPI.createTaxExemption(payload),
    updateItem: (id, payload) => ledgerAPI.updateTaxExemption(id, payload),
    deleteItem: (id) => ledgerAPI.deleteTaxExemption(id),
    deleteMessage: (item) => `Delete exemption ${item.exemption_code}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.tax_type_id !== undefined) payload.tax_type_id = Number(payload.tax_type_id);
      return payload;
    },
    validate: (values) => {
      for (const key of ['exemption_code', 'party_tin', 'tax_type_id', 'effective_from']) {
        if (values[key] === '' || values[key] === undefined || values[key] === null) {
          return `${key.replace(/_/g, ' ')} is required`;
        }
      }
      return null;
    },
  },

  branches: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Branches',
    idKey: 'branch_id',
    searchPlaceholder: 'Search branches...',
    columns: [
      { label: 'Code', render: (b) => <strong>{b.branch_code}</strong> },
      { label: 'Name', render: (b) => b.branch_name },
      { label: 'City', render: (b) => b.city || '-' },
      { label: 'Head Office', render: (b) => boolChip(b.is_head_office) },
      { label: 'Status', render: (b) => activeChip(b.is_active) },
    ],
    fields: [
      { key: 'branch_code', label: 'Code', required: true },
      { key: 'branch_name', label: 'Name', required: true },
      { key: 'is_head_office', label: 'Head Office', type: 'switch', md: 12 },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'region', label: 'Region' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      branch_code: '', branch_name: '', is_head_office: false, address: '',
      city: '', region: '', phone: '', email: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getBranches(params),
    createItem: (payload) => ledgerAPI.createBranch(payload),
    updateItem: (id, payload) => ledgerAPI.updateBranch(id, payload),
    deleteItem: (id) => ledgerAPI.deleteBranch(id),
    deleteMessage: (item) => `Delete branch ${item.branch_code}?`,
    mapToPayload: (values) => dropBlanks({ ...values }),
    validate: (values) => {
      for (const key of ['branch_code', 'branch_name']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  departments: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Departments',
    idKey: 'department_id',
    searchPlaceholder: 'Search departments...',
    columns: [
      { label: 'Code', render: (d) => <strong>{d.department_code}</strong> },
      { label: 'Name', render: (d) => d.department_name },
      { label: 'Status', render: (d) => activeChip(d.is_active) },
    ],
    fields: [
      { key: 'department_code', label: 'Code', required: true },
      { key: 'department_name', label: 'Name', required: true },
      { key: 'parent_id', label: 'Parent Department', type: 'picker', picker: DepartmentPicker },
      { key: 'branch_id', label: 'Branch', type: 'picker', picker: BranchPicker },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      department_code: '', department_name: '', parent_id: '', branch_id: '', description: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getDepartments(params),
    createItem: (payload) => ledgerAPI.createDepartment(payload),
    updateItem: (id, payload) => ledgerAPI.updateDepartment(id, payload),
    deleteItem: (id) => ledgerAPI.deleteDepartment(id),
    deleteMessage: (item) => `Delete department ${item.department_code}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      ['parent_id', 'branch_id'].forEach((k) => {
        if (payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      return payload;
    },
    validate: (values) => {
      for (const key of ['department_code', 'department_name']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  locations: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Locations',
    idKey: 'location_id',
    searchPlaceholder: 'Search locations...',
    columns: [
      { label: 'Code', render: (l) => <strong>{l.location_code}</strong> },
      { label: 'Name', render: (l) => l.location_name },
      { label: 'City', render: (l) => l.city || '-' },
      { label: 'Stock Location', render: (l) => boolChip(l.is_stock_location) },
      { label: 'Status', render: (l) => activeChip(l.is_active) },
    ],
    fields: [
      { key: 'location_code', label: 'Code', required: true },
      { key: 'location_name', label: 'Name', required: true },
      { key: 'branch_id', label: 'Branch', type: 'picker', picker: BranchPicker },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'is_stock_location', label: 'Stock Location', type: 'switch', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      location_code: '', location_name: '', branch_id: '', address: '',
      city: '', is_stock_location: false, is_active: true,
    },
    fetchList: (params) => ledgerAPI.getLocations(params),
    createItem: (payload) => ledgerAPI.createLocation(payload),
    updateItem: (id, payload) => ledgerAPI.updateLocation(id, payload),
    deleteItem: (id) => ledgerAPI.deleteLocation(id),
    deleteMessage: (item) => `Delete location ${item.location_code}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.branch_id !== undefined) payload.branch_id = Number(payload.branch_id);
      return payload;
    },
    validate: (values) => {
      for (const key of ['location_code', 'location_name']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  costCentres: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Cost Centres',
    idKey: 'cost_centre_id',
    searchPlaceholder: 'Search cost centres...',
    columns: [
      { label: 'Code', render: (c) => <strong>{c.cost_centre_code}</strong> },
      { label: 'Name', render: (c) => c.cost_centre_name },
      { label: 'Status', render: (c) => activeChip(c.is_active) },
    ],
    fields: [
      { key: 'cost_centre_code', label: 'Code', required: true },
      { key: 'cost_centre_name', label: 'Name', required: true },
      { key: 'parent_id', label: 'Parent Cost Centre', type: 'picker', picker: CostCentrePicker },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      cost_centre_code: '', cost_centre_name: '', parent_id: '', description: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getCostCentres(params),
    createItem: (payload) => ledgerAPI.createCostCentre(payload),
    updateItem: (id, payload) => ledgerAPI.updateCostCentre(id, payload),
    deleteItem: (id) => ledgerAPI.deleteCostCentre(id),
    deleteMessage: (item) => `Delete cost centre ${item.cost_centre_code}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.parent_id !== undefined) payload.parent_id = Number(payload.parent_id);
      return payload;
    },
    validate: (values) => {
      for (const key of ['cost_centre_code', 'cost_centre_name']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  dimensions: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Financial Dimensions',
    idKey: 'dimension_id',
    searchPlaceholder: 'Search dimensions...',
    columns: [
      { label: 'Code', render: (d) => <strong>{d.dimension_code}</strong> },
      { label: 'Name', render: (d) => d.dimension_name },
      { label: 'Entity Type', render: (d) => d.entity_type },
      { label: 'Status', render: (d) => activeChip(d.is_active) },
    ],
    fields: [
      { key: 'dimension_code', label: 'Code', required: true },
      { key: 'dimension_name', label: 'Name', required: true },
      { key: 'entity_type', label: 'Entity Type', required: true, helper: 'e.g. COST_CENTRE, PROJECT' },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: { dimension_code: '', dimension_name: '', entity_type: '', description: '', is_active: true },
    fetchList: (params) => ledgerAPI.getDimensions(params),
    createItem: (payload) => ledgerAPI.createDimension(payload),
    updateItem: (id, payload) => ledgerAPI.updateDimension(id, payload),
    deleteItem: (id) => ledgerAPI.deleteDimension(id),
    deleteMessage: (item) => `Delete dimension ${item.dimension_code}?`,
    mapToPayload: (values) => dropBlanks({ ...values }),
    validate: (values) => {
      for (const key of ['dimension_code', 'dimension_name', 'entity_type']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  dimensionValues: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Dimension Values',
    idKey: 'value_id',
    searchPlaceholder: 'Search values...',
    parentFilter: { key: 'dimension_id', label: 'Dimension', required: true, fetchOptions: dimensionOptions },
    columns: [
      { label: 'Code', render: (v) => <strong>{v.value_code}</strong> },
      { label: 'Name', render: (v) => v.value_name },
      { label: 'Header', render: (v) => boolChip(v.is_header) },
      { label: 'Status', render: (v) => activeChip(v.is_active) },
    ],
    fields: [
      { key: 'value_code', label: 'Code', required: true },
      { key: 'value_name', label: 'Name', required: true },
      { key: 'parent_id', label: 'Parent Value', type: 'picker', picker: DimensionValuePicker },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_header', label: 'Header Value', type: 'switch', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      value_code: '', value_name: '', parent_id: '', description: '', is_header: false, is_active: true,
    },
    fetchList: (params) => ledgerAPI.getDimensionValues(params),
    createItem: (payload) => ledgerAPI.createDimensionValue(payload),
    updateItem: (id, payload) => ledgerAPI.updateDimensionValue(id, payload),
    deleteItem: (id) => ledgerAPI.deleteDimensionValue(id),
    deleteMessage: (item) => `Delete value ${item.value_code}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.parent_id !== undefined) payload.parent_id = Number(payload.parent_id);
      return payload;
    },
    validate: (values) => {
      for (const key of ['value_code', 'value_name']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  accountingPolicies: {
    resource: RESOURCES.LEDGER.SETTINGS,
    title: 'Accounting Policies',
    idKey: 'policy_id',
    searchPlaceholder: 'Search policies...',
    columns: [
      { label: 'Code', render: (p) => <strong>{p.policy_code}</strong> },
      { label: 'Name', render: (p) => p.policy_name },
      { label: 'Group', render: (p) => p.policy_group },
      { label: 'Value', render: (p) => (p.setting_value ?? '-').toString().slice(0, 40) },
      { label: 'Status', render: (p) => activeChip(p.is_active) },
    ],
    fields: [
      { key: 'policy_code', label: 'Code', required: true },
      { key: 'policy_name', label: 'Name', required: true },
      { key: 'policy_group', label: 'Group', required: true },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'setting_value', label: 'Value', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      policy_code: '', policy_name: '', policy_group: '', description: '', setting_value: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getAccountingPolicies(params),
    createItem: (payload) => ledgerAPI.createAccountingPolicy(payload),
    updateItem: (id, payload) => ledgerAPI.updateAccountingPolicy(id, payload),
    deleteItem: (id) => ledgerAPI.deleteAccountingPolicy(id),
    deleteMessage: (item) => `Delete policy ${item.policy_code}?`,
    mapToPayload: (values) => dropBlanks({ ...values }),
    validate: (values) => {
      for (const key of ['policy_code', 'policy_name', 'policy_group']) {
        if (!String(values[key] ?? '').trim()) return `${key.replace(/_/g, ' ')} is required`;
      }
      return null;
    },
  },

  paymentMethodTypes: {
    resource: RESOURCES.LEDGER.CASH,
    title: 'Payment Method Types',
    subtitle: 'Bank/mobile-money sub-categories - each declares which identifier fields a reconciliation account of that type must carry',
    idKey: 'method_type_id',
    searchPlaceholder: 'Search types...',
    columns: [
      { label: 'Name', render: (t) => <strong>{t.display_name}</strong> },
      { label: 'Method', render: (t) => t.paymentMethod?.method_name || `#${t.method_id}` },
      {
        label: 'Requires',
        render: (t) => [
          t.requires_account_no && 'Acc #',
          t.requires_institution && 'Institution',
          t.requires_branch && 'Branch',
          t.requires_swift && 'SWIFT',
          t.requires_routing && 'Routing',
        ].filter(Boolean).join(', ') || '-',
      },
      { label: 'Status', render: (t) => activeChip(t.is_active) },
    ],
    fields: [
      { key: 'method_id', label: 'Payment Method', type: 'picker', picker: PaymentMethodPicker, required: true, md: 6 },
      { key: 'display_name', label: 'Display Name', required: true, md: 6, helper: 'e.g. "Mobile Money" or "Local Bank Transfer"' },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'requires_account_no', label: 'Requires account number', type: 'switch', md: 6 },
      { key: 'requires_institution', label: 'Requires institution name', type: 'switch', md: 6 },
      { key: 'requires_branch', label: 'Requires branch code', type: 'switch', md: 6 },
      { key: 'requires_swift', label: 'Requires SWIFT code', type: 'switch', md: 6 },
      { key: 'requires_routing', label: 'Requires routing number', type: 'switch', md: 6 },
      { key: 'icon', label: 'Icon', helper: 'Optional icon identifier for the UI', md: 6 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      method_id: '', display_name: '', description: '',
      requires_account_no: true, requires_institution: false, requires_branch: false,
      requires_swift: false, requires_routing: false, icon: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getPaymentMethodTypes(params),
    createItem: (payload) => ledgerAPI.createPaymentMethodType(payload),
    updateItem: (id, payload) => ledgerAPI.updatePaymentMethodType(id, payload),
    deleteItem: (id) => ledgerAPI.deletePaymentMethodType(id),
    deleteMessage: (item) => `Delete payment method type "${item.display_name}"? Reconciliation accounts already using it must be removed first.`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.method_id !== undefined) payload.method_id = Number(payload.method_id);
      return payload;
    },
    validate: (values) => {
      if (!values.method_id) return 'payment method is required';
      if (!String(values.display_name ?? '').trim()) return 'display name is required';
      return null;
    },
  },

  reconciliationAccounts: {
    resource: RESOURCES.LEDGER.CASH,
    title: 'Reconciliation Accounts',
    subtitle: 'Links a chart-of-accounts bank/cash account to its real-world bank details - required before it appears under Bank Accounts',
    idKey: 'detail_id',
    searchPlaceholder: 'Search account no., institution, holder...',
    columns: [
      { label: 'Account', render: (d) => <strong>{d.account?.account_code} - {d.account?.account_name}</strong> },
      { label: 'Type', render: (d) => d.payment_method_type?.display_name || `#${d.payment_method_type_id}` },
      { label: 'External No.', render: (d) => d.external_account_no },
      { label: 'Institution', render: (d) => d.institution_name || '-' },
      { label: 'Auto-recon', render: (d) => boolChip(d.auto_reconcile) },
      { label: 'Status', render: (d) => activeChip(d.is_active) },
    ],
    fields: [
      { key: 'account_id', label: 'GL Account', type: 'picker', picker: AccountPicker, required: true, md: 6, helper: 'Cannot be changed after creation - delete and recreate to relink' },
      { key: 'payment_method_type_id', label: 'Payment Method Type', type: 'picker', picker: PaymentMethodTypePicker, required: true, md: 6 },
      { key: 'currency_id', label: 'Currency (optional)', type: 'picker', picker: CurrencyPicker, md: 6 },
      { key: 'external_account_no', label: 'Account Number', required: true, md: 6 },
      { key: 'account_holder_name', label: 'Account Holder Name', md: 6 },
      { key: 'institution_name', label: 'Institution Name', md: 6 },
      { key: 'branch_code', label: 'Branch Code', md: 4 },
      { key: 'swift_code', label: 'SWIFT Code', md: 4 },
      { key: 'routing_number', label: 'Routing Number', md: 4 },
      { key: 'auto_reconcile', label: 'Auto-reconcile', type: 'switch', md: 6 },
      { key: 'reconciliation_day', label: 'Reconciliation Day (1-31)', type: 'number', min: 1, md: 6 },
      { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      account_id: '', payment_method_type_id: '', currency_id: '', external_account_no: '',
      account_holder_name: '', institution_name: '', branch_code: '', swift_code: '',
      routing_number: '', auto_reconcile: false, reconciliation_day: '', notes: '', is_active: true,
    },
    fetchList: (params) => ledgerAPI.getReconciliationAccounts(params),
    createItem: (payload) => ledgerAPI.createReconciliationAccount(payload),
    updateItem: (id, payload) => ledgerAPI.updateReconciliationAccount(id, payload),
    deleteItem: (id) => ledgerAPI.deleteReconciliationAccount(id),
    deleteMessage: (item) => `Delete reconciliation setup for ${item.account?.account_code || item.account_id}? It will disappear from Bank Accounts.`,
    mapToPayload: (values, isEdit) => {
      const payload = dropBlanks({ ...values });
      if (payload.account_id !== undefined) payload.account_id = Number(payload.account_id);
      if (payload.payment_method_type_id !== undefined) payload.payment_method_type_id = Number(payload.payment_method_type_id);
      if (payload.currency_id !== undefined) payload.currency_id = Number(payload.currency_id);
      if (payload.reconciliation_day !== undefined) payload.reconciliation_day = Number(payload.reconciliation_day);
      // account_id is immutable server-side (UpdateReconciliationDetailDto
      // doesn't declare it) - sending it on an update 400s under the
      // whitelist validator, so it's only ever sent on create.
      if (isEdit) delete payload.account_id;
      return payload;
    },
    validate: (values) => {
      if (!values.account_id) return 'GL account is required';
      if (!values.payment_method_type_id) return 'payment method type is required';
      if (!String(values.external_account_no ?? '').trim()) return 'account number is required';
      return null;
    },
  },

  matchRules: {
    resource: RESOURCES.LEDGER.CASH,
    title: 'Match Rules',
    subtitle: 'Per-account rules the bank statement matching engine consults, in priority order, before falling back to fuzzy matching',
    idKey: 'rule_id',
    searchPlaceholder: 'Search rules...',
    columns: [
      { label: 'Account', render: (r) => r.account ? `${r.account.account_code} - ${r.account.account_name}` : `#${r.account_id}` },
      { label: 'Priority', render: (r) => r.priority },
      { label: 'Direction', render: (r) => r.direction },
      { label: 'Match on', render: (r) => [r.description_contains && `"${r.description_contains}"`, r.reference_equals && `ref=${r.reference_equals}`].filter(Boolean).join(', ') || 'any' },
      { label: 'Action', render: (r) => r.action },
      { label: 'Status', render: (r) => activeChip(r.is_active) },
    ],
    fields: [
      { key: 'account_id', label: 'Bank Account', type: 'picker', picker: AccountPicker, required: true, md: 6 },
      { key: 'priority', label: 'Priority (lower runs first)', type: 'number', min: 1, md: 6 },
      { key: 'direction', label: 'Direction', type: 'select', options: [{ label: 'Any', value: 'ANY' }, { label: 'Debit (money out)', value: 'DEBIT' }, { label: 'Credit (money in)', value: 'CREDIT' }], md: 6 },
      { key: 'description_contains', label: 'Description contains', md: 6 },
      { key: 'reference_equals', label: 'Reference equals', md: 6 },
      { key: 'min_amount', label: 'Min amount', type: 'number', min: 0, md: 6 },
      { key: 'max_amount', label: 'Max amount', type: 'number', min: 0, md: 6 },
      { key: 'action', label: 'Action', type: 'select', options: [{ label: 'Auto-match to book entry', value: 'AUTO_MATCH' }, { label: 'Suggest bank charge', value: 'SUGGEST_CHARGE' }, { label: 'Suggest interest income', value: 'SUGGEST_INTEREST' }], md: 6 },
      { key: 'resolve_account_id', label: 'Resolution Account (for suggest actions)', type: 'picker', picker: AccountPicker, md: 6 },
      { key: 'tolerance_days', label: 'Date tolerance (days)', type: 'number', min: 0, md: 6 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: {
      account_id: '', priority: 100, direction: 'ANY', description_contains: '', reference_equals: '',
      min_amount: '', max_amount: '', action: 'AUTO_MATCH', resolve_account_id: '', tolerance_days: 5, is_active: true,
    },
    fetchList: (params) => ledgerAPI.getMatchRules(params),
    createItem: (payload) => ledgerAPI.createMatchRule(payload),
    updateItem: (id, payload) => ledgerAPI.updateMatchRule(id, payload),
    deleteItem: (id) => ledgerAPI.deleteMatchRule(id),
    deleteMessage: () => 'Delete this match rule?',
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      ['account_id', 'priority', 'min_amount', 'max_amount', 'resolve_account_id', 'tolerance_days'].forEach((k) => {
        if (payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      return payload;
    },
    validate: (values) => {
      if (!values.account_id) return 'bank account is required';
      if (values.action && values.action !== 'AUTO_MATCH' && !values.resolve_account_id) {
        return 'a resolution account is required for suggest actions';
      }
      if (values.min_amount !== '' && values.max_amount !== '' && Number(values.min_amount) > Number(values.max_amount)) {
        return 'min amount must not exceed max amount';
      }
      return null;
    },
  },

  expenseCategories: {
    resource: RESOURCES.LEDGER.EXPENSES,
    title: 'Expense Categories',
    subtitle: 'Claim categories, each mapped to the GL expense account it posts to',
    idKey: 'category_id',
    searchPlaceholder: 'Search categories...',
    columns: [
      { label: 'Name', render: (c) => <strong>{c.category_name}</strong> },
      { label: 'Expense Account', render: (c) => c.account?.account_name || c.expense_account_id },
      { label: 'Status', render: (c) => activeChip(c.is_active) },
    ],
    fields: [
      { key: 'category_name', label: 'Category Name', required: true, md: 12 },
      { key: 'expense_account_id', label: 'Expense Account', type: 'picker', picker: AccountPicker, required: true, md: 12 },
      { key: 'description', label: 'Description', type: 'textarea', md: 12 },
      { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
    ],
    initialValues: { category_name: '', expense_account_id: '', description: '', is_active: true },
    fetchList: (params) => ledgerAPI.getExpenseCategories(params),
    createItem: (payload) => ledgerAPI.createExpenseCategory(payload),
    updateItem: (id, payload) => ledgerAPI.updateExpenseCategory(id, payload),
    deleteItem: (id) => ledgerAPI.deleteExpenseCategory(id),
    deleteMessage: (item) => `Delete expense category ${item.category_name}?`,
    mapToPayload: (values) => {
      const payload = dropBlanks({ ...values });
      if (payload.expense_account_id !== undefined) payload.expense_account_id = Number(payload.expense_account_id);
      return payload;
    },
    validate: (values) => {
      if (!String(values.category_name ?? '').trim()) return 'category name is required';
      if (!values.expense_account_id) return 'expense account is required';
      return null;
    },
  },
};
